import { createSlice } from '@reduxjs/toolkit';
import {
  bulletinPdfDraftInitialState,
  defaultBulletinPdfDraftSaveStatus,
} from './bulletinPdfDraft.initialState';
import {
  discardBulletinPdfDraft,
  loadBulletinPdfDraft,
} from './bulletinPdfDraft.actions';

const bulletinPdfDraftSlice = createSlice({
  name: 'bulletinPdfDraftReducer',
  initialState: bulletinPdfDraftInitialState,
  reducers: {
    reset: () => bulletinPdfDraftInitialState,
    setScope: (state, action) => {
      const { bulletinId, pdfTemplate, isPageReady } = action.payload;
      state.bulletinId = bulletinId;
      state.pdfTemplate = pdfTemplate;
      state.isPageReady = isPageReady;
    },
    setPageReady: (state, action) => {
      state.isPageReady = Boolean(action.payload);
    },
    setPhase: (state, action) => {
      state.phase = action.payload;
    },
    setIntroductionEnabledLocal: (state, action) => {
      state.introductionEnabled = Boolean(action.payload);
    },
    saveStarted: (state) => {
      state.saveStatus = {
        status: 'saving',
        savedAt: null,
        error: null,
      };
    },
    saveSucceeded: (state, action) => {
      const bumpRevision = action.payload?.bumpRevision !== false;
      if (bumpRevision) {
        state.draftRevision += 1;
      }
      state.saveStatus = {
        status: 'saved',
        savedAt: Date.now(),
        error: null,
      };
    },
    saveFailed: (state, action) => {
      state.saveStatus = {
        status: 'error',
        savedAt: null,
        error: action.payload,
      };
    },
    acceptDraft: (state) => {
      state.hasPendingDraft = false;
      state.pendingDomApply = true;
      state.phase = 'applied';
      state.draftRevision += 1;
    },
    clearPendingDomApply: (state) => {
      state.pendingDomApply = false;
    },
    beginDraftLoad: (state) => {
      state.phase = 'loading';
      state.draftUpdatedAt = null;
      state.saveStatus = defaultBulletinPdfDraftSaveStatus;
      state.hasPendingDraft = false;
      state.pendingDomApply = false;
      state.introductionEnabled = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadBulletinPdfDraft.pending, (state) => {
        state.phase = 'loading';
        state.draftUpdatedAt = null;
        state.saveStatus = defaultBulletinPdfDraftSaveStatus;
        state.hasPendingDraft = false;
        state.pendingDomApply = false;
        state.introductionEnabled = false;
      })
      .addCase(loadBulletinPdfDraft.fulfilled, (state, action) => {
        const { phase, hasDraft, updatedAt } = action.payload;
        state.phase = phase;
        state.hasPendingDraft = hasDraft;
        state.draftUpdatedAt = updatedAt;
      })
      .addCase(loadBulletinPdfDraft.rejected, (state) => {
        state.phase = 'none';
        state.hasPendingDraft = false;
        state.draftUpdatedAt = null;
      })
      .addCase(discardBulletinPdfDraft.fulfilled, (state) => {
        state.pendingDomApply = false;
        state.saveStatus = defaultBulletinPdfDraftSaveStatus;
        state.hasPendingDraft = false;
        state.introductionEnabled = false;
        state.draftRevision += 1;
        state.phase = 'discarded';
      });
  },
});

export const {
  reset,
  setScope,
  setPageReady,
  setPhase,
  setIntroductionEnabledLocal,
  saveStarted,
  saveSucceeded,
  saveFailed,
  acceptDraft,
  clearPendingDomApply,
  beginDraftLoad,
} = bulletinPdfDraftSlice.actions;

export default bulletinPdfDraftSlice;
